import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError, formatTanggal } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const MAPEL_AKADEMIK = ["Matematika", "Pendidikan Pancasila", "PAI", "Bahasa Inggris", "IPAS", "Bahasa Indonesia", "Calistung", "PJOK", "IPA", "IPS"];
const MAPEL_NON = ["Seni Musik", "Seni Tari", "Seni Lukis", "Tata Boga", "TIK", "Desain Grafis", "Prakarya", "Pengembangan Diri", "OMSK", "Komunikasi", "Pramuka", "Boccee", "Athletik"];
const MAPEL_EKSTRA = ["Bahasa Reseptif", "Bahasa Ekspresif", "Preakademik", "Kemampuan Imitasi", "Bina Diri", "Kemampuan Wicara", "Calistung"];

const empty = { siswa_id: "", tahun_ajaran: "2025/2026", semester: "I", jenis: "akademik", mata_pelajaran: MAPEL_AKADEMIK[0], materi: "", sub_materi: "", tugas_pr: "", catatan: "" };
const F_EMPTY = { siswa: "", jenis: "", tahun_ajaran: "", semester: "", mapel: "" };

export function BukuBatas() {
  const [items, setItems] = useState([]);
  const [pend, setPend] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);

  const load = useCallback(async () => {
    const [{ data: bb }, { data: p }] = await Promise.all([api.get("/buku-batas"), api.get("/pendampingan")]);
    setItems(bb); setPend(p);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((b) => {
    if (applied.siswa && !(b.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    if (applied.jenis && b.jenis !== applied.jenis) return false;
    if (applied.tahun_ajaran && b.tahun_ajaran !== applied.tahun_ajaran) return false;
    if (applied.semester && b.semester !== applied.semester) return false;
    if (applied.mapel && !(b.mata_pelajaran || "").toLowerCase().includes(applied.mapel.toLowerCase())) return false;
    return true;
  });

  const mapelList = useMemo(() => (
    form.jenis === "akademik" ? MAPEL_AKADEMIK : form.jenis === "non_akademik" ? MAPEL_NON : MAPEL_EKSTRA
  ), [form.jenis]);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/buku-batas", form); toast.success("Tersimpan"); setShow(false); setForm(empty); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Layout nav={NAV_GURU}>
      <PageTitle title="Buku Batas Guru" action={<button onClick={() => setShow(true)} className="pp-btn-primary"><Plus className="w-4 h-4" />Input Buku Batas</button>} />

      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="bbatas-filter"
        fields={[
          { key: "siswa", type: "text", label: "Siswa", placeholder: "Cari nama siswa" },
          { key: "jenis", type: "select", label: "Jenis", options: [{ v: "akademik", label: "Akademik" }, { v: "non_akademik", label: "Non-Akademik" }, { v: "ekstra", label: "Ekstra" }] },
          { key: "mapel", type: "text", label: "Mapel", placeholder: "Cari mapel" },
          { key: "semester", type: "select", label: "Sem", options: [{ v: "I", label: "I" }, { v: "II", label: "II" }] },
          { key: "tahun_ajaran", type: "select", label: "TA", options: ["2023/2024", "2024/2025", "2025/2026", "2026/2027"] },
        ]}
      />
      {show && (
        <section className="pp-card p-6 mb-6 animate-fade-up">
          <div className="flex items-center justify-between mb-5"><h3 className="text-lg font-semibold">Input Buku Batas</h3><button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
            <div><label className="pp-label">Siswa *</label>
              <select required className="pp-input" value={form.siswa_id} onChange={(e) => setForm({...form, siswa_id: e.target.value})}>
                <option value="">Pilih siswa</option>{pend.map(p => <option key={p.siswa_id} value={p.siswa_id}>{p.nama_siswa}</option>)}
              </select></div>
            <div><label className="pp-label">Tahun Ajaran</label>
              <select className="pp-input" value={form.tahun_ajaran} onChange={(e) => setForm({...form, tahun_ajaran: e.target.value})}>
                <option>2023/2024</option><option>2024/2025</option><option>2025/2026</option><option>2026/2027</option>
              </select></div>
            <div><label className="pp-label">Semester</label>
              <select className="pp-input" value={form.semester} onChange={(e) => setForm({...form, semester: e.target.value})}>
                <option value="I">Semester I</option><option value="II">Semester II</option>
              </select></div>
            <div><label className="pp-label">Jenis</label>
              <select className="pp-input" value={form.jenis} onChange={(e) => setForm({...form, jenis: e.target.value, mata_pelajaran: (e.target.value === "akademik" ? MAPEL_AKADEMIK : e.target.value === "non_akademik" ? MAPEL_NON : MAPEL_EKSTRA)[0]})}>
                <option value="akademik">Akademik</option><option value="non_akademik">Non Akademik</option><option value="ekstra">Ekstra</option>
              </select></div>
            <div className="sm:col-span-2"><label className="pp-label">Mata Pelajaran</label>
              <select className="pp-input" value={form.mata_pelajaran} onChange={(e) => setForm({...form, mata_pelajaran: e.target.value})}>
                {mapelList.map(m => <option key={m}>{m}</option>)}
              </select></div>
            <div><label className="pp-label">Materi</label><input required className="pp-input" value={form.materi} onChange={(e) => setForm({...form, materi: e.target.value})} /></div>
            <div><label className="pp-label">Sub Materi</label><input className="pp-input" value={form.sub_materi} onChange={(e) => setForm({...form, sub_materi: e.target.value})} /></div>
            <div className="sm:col-span-2"><label className="pp-label">Tugas / PR</label><textarea rows={2} className="pp-input" value={form.tugas_pr} onChange={(e) => setForm({...form, tugas_pr: e.target.value})} /></div>
            <div className="sm:col-span-2"><label className="pp-label">Catatan</label><textarea rows={2} className="pp-input" value={form.catatan} onChange={(e) => setForm({...form, catatan: e.target.value})} /></div>
            <div className="sm:col-span-2 flex justify-end gap-2"><button type="button" onClick={() => setShow(false)} className="pp-btn-secondary">Batal</button><button type="submit" className="pp-btn-primary">Simpan</button></div>
          </form>
        </section>
      )}

      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Tanggal</th><th className="text-left px-5 py-3">Siswa</th><th className="text-left px-5 py-3">Guru Pendamping</th><th className="text-left px-5 py-3">Jenis</th><th className="text-left px-5 py-3">Mapel</th><th className="text-left px-5 py-3">Materi</th><th className="text-left px-5 py-3">Catatan</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-400 italic">Belum ada catatan.</td></tr>}
              {filtered.map(it => (
                <tr key={it.id} className="pp-row">
                  <td className="px-5 py-3 text-xs text-slate-500">{formatTanggal(it.created_at)}</td>
                  <td className="px-5 py-3 font-medium">{it.nama_siswa}</td>
                  <td className="px-5 py-3 text-slate-700">{it.nama_guru || "—"}</td>
                  <td className="px-5 py-3 capitalize">{it.jenis.replace("_", " ")}</td>
                  <td className="px-5 py-3">{it.mata_pelajaran}</td>
                  <td className="px-5 py-3 text-slate-700">{it.materi} {it.sub_materi && <span className="text-slate-400 italic">/ {it.sub_materi}</span>}</td>
                  <td className="px-5 py-3 text-xs text-slate-600">{it.catatan || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}
